import { useState, useEffect } from 'react';
import { Mail, CheckCircle, XCircle, Eye, RefreshCw, Trash2, AlertTriangle } from 'lucide-react';
import { getEmailHistory, deleteEmailHistory } from '../../lib/db';
import { EmailHistoryRecord } from '../../types';

export function EmailHistory() {
  const [records, setRecords] = useState<EmailHistoryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'sent' | 'opened' | 'failed'>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<{ ids: string[]; message: string } | null>(null);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const data = await getEmailHistory();
      setRecords(data);
    } catch (error) {
      console.error('Error cargando historial:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const filtered = filter === 'all' ? records : records.filter(r => r.status === filter);

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(r => r.id)));
    }
  };

  const handleDelete = (ids: string[]) => {
    if (ids.length === 0) return;
    const message = ids.length === 1
      ? '¿Eliminar este registro del historial?'
      : `¿Eliminar ${ids.length} registros seleccionados del historial?`;
    setDeleteConfirm({ ids, message });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteEmailHistory(deleteConfirm.ids);
      setRecords(prev => prev.filter(r => !deleteConfirm.ids.includes(r.id)));
      setSelected(prev => {
        const next = new Set(prev);
        deleteConfirm.ids.forEach(id => next.delete(id));
        return next;
      });
    } catch (err) {
      console.error('Error eliminando historial:', err);
    } finally {
      setDeleteConfirm(null);
    }
  };

  const stats = {
    total: records.length,
    sent: records.filter(r => r.status === 'sent').length,
    opened: records.filter(r => r.status === 'opened').length,
    failed: records.filter(r => r.status === 'failed').length,
  };

  const statusIcon = (status: string) => {
    switch (status) {
      case 'opened': return <Eye className="w-4 h-4 text-green-600" />;
      case 'sent': return <CheckCircle className="w-4 h-4 text-blue-600" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-600" />;
      default: return <Mail className="w-4 h-4 text-gray-400" />;
    }
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case 'opened': return 'Abierto';
      case 'sent': return 'Enviado';
      case 'failed': return 'Error';
      default: return status;
    }
  };

  const statusBadgeClass = (status: string) => {
    switch (status) {
      case 'opened': return 'bg-green-100 text-green-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Historial de Envíos</h2>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <button
              onClick={() => handleDelete(Array.from(selected))}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
            >
              <Trash2 className="w-4 h-4" />
              Eliminar ({selected.size})
            </button>
          )}
          <button
            onClick={loadHistory}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total', value: stats.total, color: 'bg-slate-100 text-slate-700', filterKey: 'all' as const },
          { label: 'Enviados', value: stats.sent, color: 'bg-blue-50 text-blue-700', filterKey: 'sent' as const },
          { label: 'Abiertos', value: stats.opened, color: 'bg-green-50 text-green-700', filterKey: 'opened' as const },
          { label: 'Errores', value: stats.failed, color: 'bg-red-50 text-red-700', filterKey: 'failed' as const },
        ].map(stat => (
          <button
            key={stat.label}
            onClick={() => setFilter(stat.filterKey)}
            className={`p-3 rounded-lg text-center transition-all ${stat.color} ${
              filter === stat.filterKey ? 'ring-2 ring-indigo-400' : ''
            }`}
          >
            <p className="text-2xl font-bold">{stat.value}</p>
            <p className="text-xs font-medium">{stat.label}</p>
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-12 text-center text-slate-400">Cargando...</div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-slate-400">
          <Mail className="mx-auto mb-2 w-8 h-8" />
          <p>No hay registros de envíos</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-full text-sm divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                  />
                </th>
                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-slate-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-slate-500 uppercase">Alumno</th>
                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-slate-500 uppercase">Destinatario</th>
                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-slate-500 uppercase">Asunto</th>
                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-slate-500 uppercase">Evaluación</th>
                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-slate-500 uppercase">Fecha</th>
                <th className="px-3 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {filtered.map(record => (
                <tr key={record.id} className={`hover:bg-slate-50 ${selected.has(record.id) ? 'bg-indigo-50/50' : ''}`}>
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(record.id)}
                      onChange={() => toggleSelect(record.id)}
                      className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                    />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(record.status)}`}>
                      {statusIcon(record.status)}
                      {statusLabel(record.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{record.studentName || '—'}</td>
                  <td className="px-4 py-3 text-slate-500">{record.recipientEmail}</td>
                  <td className="px-4 py-3 text-slate-700 max-w-[200px] truncate">{record.subject}</td>
                  <td className="px-4 py-3 text-slate-500">{record.evaluationPeriod ? `${record.evaluationPeriod}ª Eval.` : '—'}</td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                    {new Date(record.createdAt).toLocaleString('es-ES', {
                      day: '2-digit', month: '2-digit', year: '2-digit',
                      hour: '2-digit', minute: '2-digit'
                    })}
                  </td>
                  <td className="px-3 py-3">
                    <button
                      onClick={() => handleDelete([record.id])}
                      className="p-1 text-slate-300 rounded hover:text-red-500 hover:bg-red-50"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de confirmación */}
      {deleteConfirm && (
        <div className="flex fixed inset-0 z-50 justify-center items-center p-4">
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteConfirm(null)} />
          <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl animate-in fade-in zoom-in">
            <div className="p-6 text-center">
              <div className="flex justify-center items-center mx-auto mb-4 w-12 h-12 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-slate-900">Confirmar eliminación</h3>
              <p className="text-sm text-slate-500">{deleteConfirm.message}</p>
              <p className="mt-1 text-xs text-slate-400">Esta acción no se puede deshacer.</p>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
